import { chainConstants, type ChainflipChain } from '@chainflip/utils/chainflip';
import { intervalToDurationWords } from '@chainflip/utils/date';
import { isNullish } from '@chainflip/utils/guard';
import { subMilliseconds } from 'date-fns';
import { type SwapFailureReason } from '@/shared/graphql/generated/explorer/graphql';

const EGRESS_BROADCAST_SIGNING_DURATION_MS = 90 * 1000;

export const getSwapCompletionTime = ({
  depositChannelCreationTimestamp,
  preDepositBlockTimestamp,
  depositTimestamp,
  egressTimestamp,
  sourceChain,
}: {
  depositTimestamp: Date;
  egressTimestamp: Date;
  sourceChain: ChainflipChain;
  depositChannelCreationTimestamp?: Date;
  preDepositBlockTimestamp?: Date;
}) => {
  let millisecondsToSubtract;
  if (preDepositBlockTimestamp) {
    const preDepositTimestamp = depositChannelCreationTimestamp
      ? new Date(
          Math.max(preDepositBlockTimestamp.getTime(), depositChannelCreationTimestamp.getTime()),
        )
      : preDepositBlockTimestamp;

    millisecondsToSubtract = (depositTimestamp.getTime() - preDepositTimestamp.getTime()) / 2;
  } else {
    millisecondsToSubtract = (chainConstants[sourceChain].blockTimeSeconds / 2) * 1000;
  }

  const startMs = subMilliseconds(depositTimestamp, millisecondsToSubtract).getTime();
  const endMs = egressTimestamp.getTime() + EGRESS_BROADCAST_SIGNING_DURATION_MS;

  return intervalToDurationWords({ start: startMs, end: endMs });
};

export const getOraclePriceDeltaColor = (deltaBps: number) => {
  const lowPriceDeltaBps = -1000; // -10%
  const highPriceDeltaBps = -100; // -1%

  if (deltaBps <= lowPriceDeltaBps) return 'text-cf-red-2';
  if (deltaBps >= highPriceDeltaBps) return 'text-cf-green-3';
  return 'text-cf-white';
};

export const getSwapFailureReasonValue = (reason: SwapFailureReason | null | undefined) => {
  const swapFailureReasonValue = {
    MinPriceViolation: 'the minimum payout',
    OraclePriceSlippageExceeded: 'the live price slippage tolerance',
    OraclePriceStale: 'the current oracle price',
    PredecessorSwapFailure: 'the predecessor swap state',
    PriceImpactLimit: 'the allowed price impact limit',
    SafeModeActive: 'the required safe mode state',
  } as Record<SwapFailureReason, string>;

  const legacySwapFailureReasonValue = 'the slippage tolerance settings';
  const unknownSwapFailureReasonValue = 'the swap conditions';

  return isNullish(reason)
    ? legacySwapFailureReasonValue
    : (swapFailureReasonValue[reason] ?? unknownSwapFailureReasonValue);
};
